"use client";

import React, { useTransition } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { motion } from "framer-motion";
import dynamic from "next/dynamic";

const AdminPanel = dynamic(() => import("./AdminPanel"), { ssr: false });

export default function PortfolioFilters({
  categoriesProp,
  activeCategoryExternal,
  onCategoryChangeExternal,
}: {
  categoriesProp?: string[];
  activeCategoryExternal?: string;
  onCategoryChangeExternal?: (category: string) => void;
}) {
  const defaultCats = ["All Projects", "Web", "App", "Python"];
  const categories =
    categoriesProp && categoriesProp.length ? categoriesProp : defaultCats;
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();
  const [isPending, startTransition] = useTransition();

  const activeCategory =
    activeCategoryExternal || searchParams.get("category") || "All Projects";

  const [clickCount, setClickCount] = React.useState(0);
  const [showAdmin, setShowAdmin] = React.useState(false);
  const clickTimer = React.useRef<number | null>(null);

  const [originalPrice, setOriginalPrice] = React.useState<string>("");

  function handleCategory(category: string) {
    if (onCategoryChangeExternal) {
      onCategoryChangeExternal(category);
      return;
    }

    const params = new URLSearchParams(searchParams);
    if (category !== "All Projects") {
      params.set("category", category);
    } else {
      params.delete("category");
    }

    startTransition(() => {
      replace(`${pathname}?${params.toString()}`, { scroll: false });
    });
  }

  function handleSecretClick(category: string) {
    if (category.toLowerCase() !== "python") {
      setClickCount(0);
      if (clickTimer.current) window.clearTimeout(clickTimer.current);
      return;
    }

    setClickCount((c) => c + 1);
    if (clickTimer.current) window.clearTimeout(clickTimer.current);
    clickTimer.current = window.setTimeout(() => setClickCount(0), 3000);
    if (clickCount + 1 >= 8) {
      setShowAdmin(true);
      setClickCount(0);
      if (clickTimer.current) window.clearTimeout(clickTimer.current);
    }
  }

  

  return (
    <div
      className={`flex flex-wrap items-center gap-4 md:gap-8 border-b border-surface-variant pb-4 w-full justify-center overflow-x-auto transition-opacity ${isPending ? "opacity-50" : "opacity-100"}`}
    >
      {categories.map((category) => {
        const isActive = category === activeCategory;

        return (
          <button
            key={category}
            aria-pressed={isActive}
            className={
              isActive
                ? "font-label-sm text-label-sm uppercase tracking-widest text-primary relative pb-1 -mb-[17px] text-[14px] font-bold"
                : "font-label-sm text-label-sm uppercase tracking-widest text-secondary hover:text-primary transition-colors pb-1"
            }
            onClick={() => {
              handleCategory(category);
              handleSecretClick(category);
            }}
            type="button"
          >
            {category}
            {isActive && (
              <motion.div
                layoutId="active-category-minimal"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
              />
            )}
          </button>
        );
      })}

      {showAdmin && (
        <AdminPanel
          onClose={() => setShowAdmin(false)}
          originalPrice={originalPrice}
          setOriginalPrice={setOriginalPrice}
        />
      )}
    </div>
  );
}
